/*
 * Used machines — Horizon collection-style UI. Liste + detaljer: /apps/ib-konfigurator/machines
 *
 * Pålidelig Mascus-berigelse (alle billeder + beskrivelse) kræver at app-proxy’en kan hente HTML
 * server-side (undgår CORS). Understøttet kald (implementér i ib-konfigurator):
 *   GET /apps/ib-konfigurator/machines?shop=SHOP&mascusPageUrl=ENCODED_MASCUS_PRODUCT_URL
 *   Response JSON: { "html": "<!DOCTYPE ..." } eller { "mascusHtml": "..." }
 * Valgfrit: inkl. feltet mascusHtml på detail-JSON (samme HTML), så kunden ikke afhænger af klient-fetch.
 */
(function () {
  'use strict';

  /** Offentlige CORS-proxyer (browser). corsproxy blokerer ofte server-requests; allorigins først. */
  var DETAIL_HTML_PROXIES = ['https://api.allorigins.win/raw?url=', 'https://corsproxy.io/?url='];

  /** Max ventetid pr. fetch (for korte timeouts gav tom side når proxy/app var langsom). */
  var DETAIL_FETCH_TIMEOUT_MS = 22000;
  var APP_PROXY_FETCH_TIMEOUT_MS = 20000;

  function fetchWithTimeout(url, init, timeoutMs) {
    var ctrl = new AbortController();
    var t = setTimeout(function () {
      ctrl.abort();
    }, timeoutMs);
    var parent = init && init.signal;
    if (parent) {
      if (parent.aborted) {
        clearTimeout(t);
        return Promise.reject(Object.assign(new Error('Aborted'), { name: 'AbortError' }));
      }
      parent.addEventListener(
        'abort',
        function () {
          clearTimeout(t);
          ctrl.abort();
        },
        { once: true }
      );
    }
    return fetch(url, Object.assign({}, init, { signal: ctrl.signal })).finally(function () {
      clearTimeout(t);
    });
  }

  window.__IB_UM_POP_CALLBACKS__ = window.__IB_UM_POP_CALLBACKS__ || [];
  var ibUmPopstateCallbacks = window.__IB_UM_POP_CALLBACKS__;
  if (!window.__IB_UM_POPSTATE_READY__) {
    window.__IB_UM_POPSTATE_READY__ = true;
    window.addEventListener('popstate', function () {
      (window.__IB_UM_POP_CALLBACKS__ || []).slice().forEach(function (fn) {
        try {
          fn();
        } catch (e) {
          /* ignore */
        }
      });
    });
  }

  function qs(root, sel) {
    return root.querySelector(sel);
  }

  function esc(value) {
    var d = document.createElement('div');
    d.textContent = value == null ? '' : String(value);
    return d.innerHTML;
  }

  function getHighRes(url) {
    if (!url) return '';
    var out = String(url);
    out = out.replace('/image/product/small/', '/image/product/large/');
    out = out.replace('/image/product/medium/', '/image/product/large/');
    out = out.replace('/small/', '/large/');
    out = out.replace('/medium/', '/large/');
    return out;
  }

  /**
   * Next.js billed-URL på mascus.dk (/_next/image?url=…) virker ikke stabilt som img src fra Shopify
   * (optimizer, referer, cache). Pak altid ud til den rå CDN-URL.
   */
  function unwrapMascusImageUrl(url) {
    if (!url) return '';
    var s = cleanText(String(url));
    if (!s) return '';
    if (s.indexOf('//') === 0) s = 'https:' + s;
    if (s.indexOf('/_next/image') === -1 && s.indexOf('_next/image?') === -1) return s;
    try {
      var absolute =
        s.indexOf('http') === 0 ? s : 'https://www.mascus.dk' + (s.charAt(0) === '/' ? s : '/' + s);
      var parsed = new URL(absolute);
      if (parsed.pathname.indexOf('/_next/image') === -1) return s;
      var inner = parsed.searchParams.get('url');
      if (!inner) return s;
      var decoded = decodeURIComponent(inner);
      if (/^https?:\/\//i.test(decoded)) return decoded;
      return 'https://www.mascus.dk' + (decoded.charAt(0) === '/' ? decoded : '/' + decoded);
    } catch (_e) {
      return s;
    }
  }

  /** Én kanonisk URL til <img src> (cloudfront / direkte fil). */
  function normalizeImageUrlForDisplay(url) {
    if (!url) return '';
    var s = unwrapMascusImageUrl(url);
    s = getHighRes(s);
    return cleanText(s);
  }

  function normalizeImageEntry(entry) {
    if (!entry) return '';
    if (typeof entry === 'string') return normalizeImageUrlForDisplay(entry);
    if (typeof entry === 'object') {
      return normalizeImageUrlForDisplay(
        firstValue([entry.url, entry.src, entry.image, entry.original, entry.large, entry.medium])
      );
    }
    return '';
  }

  function getMachineImages(machine) {
    if (!machine || typeof machine !== 'object') return [];

    var candidates = [];
    [
      machine.images,
      machine.pictures,
      machine.imageUrls,
      machine.gallery,
      machine.galleryImages,
      machine.photos
    ].forEach(function (source) {
      if (Array.isArray(source)) {
        source.forEach(function (entry) {
          var normalized = normalizeImageEntry(entry);
          if (normalized) candidates.push(normalized);
        });
      }
    });

    var single = firstValue([machine.image, machine.mainImage, machine.thumbnail]);
    if (single) candidates.push(normalizeImageUrlForDisplay(single));

    return uniqueStrings(candidates.filter(Boolean));
  }

  function getMachineDescription(machine) {
    if (!machine || typeof machine !== 'object') return '';
    return firstValue([
      machine.description,
      machine.descriptionText,
      machine.longDescription,
      machine.body,
      machine.details
    ]);
  }

  function buildDetailUrl(path, id) {
    var base = path || window.location.pathname;
    var p = new URLSearchParams();
    p.set('machine', id);
    return base + '?' + p.toString();
  }

  /** Shop money format (Liquid `shop.money_format`), set on mount so prices match products. */
  var moneyFormat = '';

  /** Group digits with `sep` every three, e.g. 175000 → "175.000". */
  function groupThousands(intDigits, sep) {
    return intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  }

  /**
   * Renders `n` the way Shopify's `money` filter would, from the shop's own money_format —
   * so machine prices read identically to product prices for every visitor, regardless of
   * browser locale. Falls back to a plain grouped number if the format can't be parsed.
   */
  function formatWithShopMoneyFormat(n, format) {
    var m = String(format).match(/\{\{\s*([a-z_]+)\s*\}\}/i);
    if (!m) return null;
    var placeholder = m[1].toLowerCase();
    var separators = { thousands: ',', decimal: '.' };
    if (placeholder.indexOf('comma_separator') !== -1) separators = { thousands: '.', decimal: ',' };
    else if (placeholder.indexOf('apostrophe_separator') !== -1) separators = { thousands: "'", decimal: '.' };
    var noDecimals = placeholder.indexOf('no_decimals') !== -1;

    var fixed = Math.abs(n).toFixed(noDecimals ? 0 : 2);
    var parts = fixed.split('.');
    var rendered = groupThousands(parts[0], separators.thousands);
    if (parts[1]) rendered += separators.decimal + parts[1];
    if (n < 0) rendered = '-' + rendered;
    return String(format).replace(m[0], rendered);
  }

  function formatPrice(price, currency) {
    if (price == null || price === '') return '';
    var n = Number(String(price).replace(',', '.'));
    if (!Number.isFinite(n)) return String(price);
    if (moneyFormat) {
      var shopFormatted = formatWithShopMoneyFormat(n, moneyFormat);
      if (shopFormatted) return shopFormatted;
    }
    try {
      if (currency) {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: String(currency) }).format(n);
      }
    } catch (_) {}
    return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(n);
  }

  function parsePriceNumber(rawPrice) {
    if (rawPrice == null || rawPrice === '') return null;
    if (typeof rawPrice === 'number') {
      return Number.isFinite(rawPrice) ? rawPrice : null;
    }
    var normalized = String(rawPrice)
      .replace(/\s+/g, '')
      .replace(/[^\d,.-]/g, '')
      .replace(',', '.');
    if (!normalized) return null;
    var parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  function shouldShowInquiryPrice(rawPrice) {
    var parsedPrice = parsePriceNumber(rawPrice);
    return parsedPrice === 0;
  }

  function extractMachineDescription(machine) {
    if (!machine || typeof machine !== 'object') return '';

    var htmlCandidates = [
      machine.descriptionHtml,
      machine.description_html,
      machine.longDescriptionHtml,
      machine.long_description_html,
      machine.detailsHtml,
      machine.details_html,
      machine.contentHtml,
      machine.content_html
    ];
    for (var i = 0; i < htmlCandidates.length; i += 1) {
      var html = htmlCandidates[i];
      if (typeof html === 'string' && cleanText(html)) return html;
    }

    var nestedObjects = [machine.details, machine.content, machine.specification, machine.specifications];
    for (var j = 0; j < nestedObjects.length; j += 1) {
      var obj = nestedObjects[j];
      if (!obj || typeof obj !== 'object') continue;
      if (typeof obj.descriptionHtml === 'string' && cleanText(obj.descriptionHtml)) return obj.descriptionHtml;
      if (typeof obj.description === 'string' && cleanText(obj.description)) return obj.description;
      if (typeof obj.text === 'string' && cleanText(obj.text)) return obj.text;
    }

    var textCandidates = [
      machine.description,
      machine.longDescription,
      machine.long_description,
      machine.summary,
      machine.text,
      machine.content
    ];
    for (var k = 0; k < textCandidates.length; k += 1) {
      var text = textCandidates[k];
      if (typeof text === 'string' && cleanText(text)) return text;
    }

    return '';
  }

  function normalizeImageCandidate(value) {
    if (!value) return '';
    if (typeof value === 'string') return normalizeImageUrlForDisplay(value);
    if (typeof value !== 'object') return '';

    var objectCandidates = [
      value.url,
      value.src,
      value.imageUrl,
      value.large,
      value.largeUrl,
      value.original,
      value.originalUrl,
      value.full,
      value.fullUrl,
      value.href
    ];
    for (var i = 0; i < objectCandidates.length; i += 1) {
      var candidate = objectCandidates[i];
      if (typeof candidate === 'string' && cleanText(candidate)) {
        return normalizeImageUrlForDisplay(candidate);
      }
    }
    return '';
  }

  function extractMachineImages(machine) {
    if (!machine || typeof machine !== 'object') return [];

    var imageCandidates = [];
    var arrayCandidates = [
      machine.images,
      machine.imageUrls,
      machine.image_urls,
      machine.gallery,
      machine.galleryImages,
      machine.gallery_images,
      machine.photos,
      machine.media
    ];

    arrayCandidates.forEach(function (entry) {
      if (!Array.isArray(entry)) return;
      entry.forEach(function (item) {
        imageCandidates.push(item);
      });
    });

    if (machine.primaryImage) imageCandidates.push(machine.primaryImage);
    if (machine.featuredImage) imageCandidates.push(machine.featuredImage);
    if (machine.image) imageCandidates.push(machine.image);
    if (machine.imageUrl) imageCandidates.push(machine.imageUrl);
    if (machine.thumbnail) imageCandidates.push(machine.thumbnail);

    return uniqueStrings(
      imageCandidates
        .map(normalizeImageCandidate)
        .filter(function (url) {
          return !!cleanText(url);
        })
    );
  }

  function extractMachineImageCount(machine, fallbackCount) {
    var countCandidates = [
      machine && machine.imageCount,
      machine && machine.imagesCount,
      machine && machine.totalImageCount,
      machine && machine.totalImages,
      machine && machine.mediaCount,
      machine && machine.photoCount
    ];
    for (var i = 0; i < countCandidates.length; i += 1) {
      var n = Number(countCandidates[i]);
      if (Number.isFinite(n) && n >= 0) return n;
    }
    return fallbackCount;
  }

  function getListState(defaultSort) {
    var u = new URL(window.location.href);
    return {
      page: Math.max(1, parseInt(u.searchParams.get('page') || '1', 10) || 1),
      q: u.searchParams.get('q') || '',
      categoryName: u.searchParams.get('categoryName') || '',
      sort: u.searchParams.get('sort') || defaultSort || 'newest'
    };
  }

  function writeListStateToUrl(state) {
    var u = new URL(window.location.href);
    ['page', 'q', 'categoryName', 'sort', 'machine', 'slug'].forEach(function (k) {
      u.searchParams.delete(k);
    });
    if (state.page > 1) u.searchParams.set('page', String(state.page));
    if (state.q) u.searchParams.set('q', state.q);
    if (state.categoryName) u.searchParams.set('categoryName', state.categoryName);
    if (state.sort) u.searchParams.set('sort', state.sort);
    window.history.replaceState({}, '', u.toString());
  }

  function getMachineFromUrl() {
    return new URL(window.location.href).searchParams.get('machine') || '';
  }

  function fetchJson(url, opts) {
    return fetch(url, opts).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    });
  }

  function uniqueStrings(values) {
    var out = [];
    var seen = new Set();
    values.forEach(function (value) {
      var v = cleanText(value);
      if (!v || seen.has(v)) return;
      seen.add(v);
      out.push(v);
    });
    return out;
  }

  function extractImageUrlsFromDetailHtml(html) {
    var matches = [];
    if (!html) return matches;

    /** Mascus bruger nu bl.a. /imagetilewm/product/ og /image/product/cust/... — ikke kun small|medium|large. */
    var cloudfrontProductPattern =
      /https:\/\/dnge9sb91helb\.cloudfront\.net\/(?:image\/product\/|imagetilewm\/product\/)[^\s"'<>()]+/gi;

    /**
     * Next/Image: ofte relativ sti "/_next/image?url=…" (domænet er ikke i HTML).
     * Understøtter også ældre absolutte https://www.mascus…/_next/image?url=
     */
    var nextImagePattern = /(?:https?:\/\/[^/\s]+)?\/_next\/image\?url=([^&"'\s)]+)/gi;

    var genericImagePattern = /https:\/\/[^"'\s)]+?\.(?:jpg|jpeg|png|webp)/gi;

    var cfMatch;
    while ((cfMatch = cloudfrontProductPattern.exec(html)) !== null) {
      matches.push(cfMatch[0]);
    }

    var nextMatch;
    while ((nextMatch = nextImagePattern.exec(html)) !== null) {
      try {
        matches.push(decodeURIComponent(nextMatch[1]));
      } catch (_error) {
        matches.push(nextMatch[1]);
      }
    }

    var genericMatch;
    while ((genericMatch = genericImagePattern.exec(html)) !== null) {
      if (genericMatch[0].indexOf('dnge9sb91helb.cloudfront.net/') > -1) {
        matches.push(genericMatch[0]);
      }
    }

    return uniqueStrings(matches.map(normalizeImageUrlForDisplay).filter(isLikelyProductImage));
  }

  /**
   * Frasortér Mascus' egne UI-ikoner. De serveres gennem Next's billed-optimizer
   * (/_next/image?url=%2F_next%2Fstatic%2Fmedia%2FRB_icon…), så udpakningen giver
   * en RELATIV sti, browseren opløser mod vores eget domæne — og 6 af 7
   * thumbnails endte som tomme billedrammer med alt-teksten synlig.
   */
  function isLikelyProductImage(url) {
    if (!url) return false;
    var s = String(url);
    // Next's statiske assets er altid tema-ikoner, aldrig maskinbilleder.
    if (s.indexOf('/_next/static/') > -1) return false;
    // Kun absolutte URL'er kan hentes herfra; en relativ sti peger på os selv.
    return /^https?:\/\//i.test(s);
  }

  /**
   * Jina/markdown-proxy kan returnere sidefod med [Mascus AT](url)… — det må ikke vises som produkttekst.
   * Skal ligge før extractLdProductExtras (kaldsorden / ældre motorer).
   */
  function isGarbageProductDescription(text) {
    if (!text || typeof text !== 'string') return true;
    var t = String(text);
    if (t.length > 12000) return true;
    var mdLinks = (t.match(/\]\(https?:\/\//g) || []).length;
    var mascusLinkLabels = (t.match(/\[Mascus/gi) || []).length;
    if (mascusLinkLabels >= 2) return true;
    if (mdLinks >= 5) return true;
    if (mdLinks >= 2 && t.length > 500) return true;
    if (/mascus\.(dk|com|de|at|co\.uk|nl|pl|se|no|fi)\b/i.test(t) && mdLinks >= 3) return true;
    return false;
  }

  /**
   * Henter beskrivelse og evt. billed-URL'er fra Mascus JSON-LD (schema.org Product) eller meta description.
   * Bruges når ib-konfigurator-feedet ikke leverer fuld tekst.
   */
  function extractLdProductExtras(html) {
    var out = { description: '', images: [] };
    if (!html) return out;

    var ldRe = /<script[^>]*type=["']application\/ld\+json["'][^>]*>([\s\S]*?)<\/script>/gi;
    var scriptChunk;
    while ((scriptChunk = ldRe.exec(html)) !== null) {
      try {
        var data = JSON.parse(scriptChunk[1]);
        var items = Array.isArray(data) ? data : [data];
        for (var i = 0; i < items.length; i += 1) {
          var item = items[i];
          if (!item || item['@type'] !== 'Product') continue;
          if (!out.description && typeof item.description === 'string' && cleanText(item.description)) {
            out.description = String(item.description);
          }
          if (item.image) {
            if (Array.isArray(item.image)) {
              item.image.forEach(function (img) {
                if (typeof img === 'string' && cleanText(img)) out.images.push(normalizeImageUrlForDisplay(img));
                else if (img && typeof img.url === 'string' && cleanText(img.url)) {
                  out.images.push(normalizeImageUrlForDisplay(img.url));
                }
              });
            } else if (typeof item.image === 'string' && cleanText(item.image)) {
              out.images.push(normalizeImageUrlForDisplay(item.image));
            }
          }
        }
      } catch (_e) {
        /* ignore malformed JSON */
      }
    }
    if (!out.description) {
      var metaMatch = html.match(/<meta[^>]+name=["']description["'][^>]+content=["']([^"']*)["']/i);
      if (metaMatch && cleanText(metaMatch[1])) {
        var metaDesc = metaMatch[1].replace(/&amp;/g, '&').replace(/&quot;/g, '"').replace(/&#39;/g, "'");
        if (!isGarbageProductDescription(metaDesc)) {
          out.description = metaDesc;
        }
      }
    }
    out.images = uniqueStrings(out.images.filter(Boolean));
    if (out.description && isGarbageProductDescription(out.description)) {
      out.description = '';
    }
    return out;
  }

  function toSafeDescriptionHtml(plain) {
    var t = cleanText(plain);
    if (!t) return '';
    return '<p>' + esc(t) + '</p>';
  }

  /** Undgår at behandle tomt feed/placeholder som “har beskrivelse” (så vi stadig henter Mascus-HTML). */
  function hasMeaningfulDescription(machine) {
    var raw = extractMachineDescription(machine);
    if (!cleanText(raw)) return false;
    var stripped = String(raw)
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (isGarbageProductDescription(stripped)) return false;
    var lower = stripped.toLowerCase();
    if (lower.indexOf('ingen yderligere oplysninger fra mascus') !== -1) return false;
    if (lower.indexOf('ingen yderligere oplysninger') !== -1 && lower.indexOf('mascus') !== -1) {
      return false;
    }
    return true;
  }

  /**
   * Fletter tekst og billed-URLer ud fra rå HTML (eller Jina-markdown med "Markdown Content:").
   * Muterer machine.images og machine.descriptionHtml når der findes bedre data.
   */
  /**
   * Ved Jina-markdown: filtrer åbenlyst forkerte CDN-URLer (footer/logo), men vær mindre stram end før —
   * ellers forsvandt alle billeder.
   */
  function isLikelyProductCdnImageUrl(url) {
    if (!url) return false;
    var u = String(url);
    if (u.indexOf('dnge9sb91helb.cloudfront.net') === -1) return true;
    if (/\/(?:imagetilewm|image)\/product\//i.test(u)) return true;
    if (/\.(jpe?g|png|webp)(\?|[#]|$)/i.test(u)) return true;
    return false;
  }

  function applyEnrichmentFromHtml(machine, html) {
    if (!machine || !html) return { merged: extractMachineImages(machine), descHtml: '' };
    var currentImages = extractMachineImages(machine);
    var fromJinaMarkdown = html.indexOf('Markdown Content:') !== -1;
    var ldExtras = extractLdProductExtras(html);
    if (fromJinaMarkdown) {
      var jinaExtras = extractFromJinaMarkdownFallback(html);
      if (jinaExtras.description && !isGarbageProductDescription(jinaExtras.description)) {
        ldExtras.description = jinaExtras.description;
      }
      ldExtras.images = uniqueStrings(ldExtras.images.concat(jinaExtras.images));
    }
    if (ldExtras.description && isGarbageProductDescription(ldExtras.description)) {
      ldExtras.description = '';
    }
    var extracted = extractImageUrlsFromDetailHtml(html);
    var merged = uniqueStrings(currentImages.concat(ldExtras.images).concat(extracted));
    if (fromJinaMarkdown) {
      merged = merged.filter(isLikelyProductCdnImageUrl);
    }
    var descHtml = '';
    if (
      ldExtras.description &&
      !hasMeaningfulDescription(machine) &&
      !isGarbageProductDescription(ldExtras.description)
    ) {
      descHtml = toSafeDescriptionHtml(ldExtras.description);
      if (descHtml) machine.descriptionHtml = descHtml;
    }
    if (merged.length > currentImages.length) {
      machine.images = merged;
    }
    return { merged: merged, descHtml: descHtml, currentImages: currentImages };
  }

  /**
   * Jina returnerer markdown uden JSON-LD — vi henter kun billed-URLer, ikke “længste linje” (sidefod).
   */
  function extractFromJinaMarkdownFallback(html) {
    var out = { description: '', images: [] };
    if (!html || html.indexOf('Markdown Content:') === -1) return out;
    var body = html.split('Markdown Content:')[1] || '';
    var ld = extractLdProductExtras(body);
    if (ld.description && !isGarbageProductDescription(ld.description)) {
      out.description = ld.description;
    }
    out.images = uniqueStrings(out.images.concat(ld.images).concat(extractImageUrlsFromDetailHtml(body)));
    return out;
  }

  /** Samme origin: ib-konfigurator-appen kan (bør) hente Mascus HTML server-side og undgå CORS. */
  function fetchMascusHtmlViaAppProxy(shop, detailUrl, signal) {
    var params = new URLSearchParams();
    params.set('shop', shop);
    params.set('mascusPageUrl', detailUrl);
    return fetchWithTimeout(
      '/apps/ib-konfigurator/machines?' + params.toString(),
      {
        credentials: 'same-origin',
        signal: signal,
        headers: { Accept: 'application/json' }
      },
      APP_PROXY_FETCH_TIMEOUT_MS
    )
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (d) {
        if (d && typeof d.html === 'string' && d.html.length > 80) return d.html;
        if (d && typeof d.mascusHtml === 'string' && d.mascusHtml.length > 80) return d.mascusHtml;
        if (d && typeof d.body === 'string' && d.body.length > 80) return d.body;
        throw new Error('no html in app proxy response');
      });
  }

  /**
   * Henter Mascus produktside som tekst. Direkte fetch til mascus.dk fejler som regel i browser (CORS),
   * så vi prøver: app-proxy → allorigins → corsproxy → direkte.
   * Jina (r.jina.ai) er fjernet: langsom og gav ofte markdown-sidefod som “beskrivelse”.
   */
  function fetchMascusHtml(shop, detailUrl, signal) {
    return fetchMascusHtmlViaAppProxy(shop, detailUrl, signal).catch(function () {
      return fetchDetailHtmlViaPublicProxies(detailUrl, signal);
    });
  }

  function fetchDetailHtmlViaPublicProxies(url, signal) {
    var proxyQueue = Promise.reject(new Error('proxy-fallback-start'));

    DETAIL_HTML_PROXIES.forEach(function (proxyPrefix) {
      proxyQueue = proxyQueue.catch(function () {
        var target = proxyPrefix + encodeURIComponent(url);
        return fetchWithTimeout(
          target,
          { credentials: 'omit', signal: signal },
          DETAIL_FETCH_TIMEOUT_MS
        ).then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        });
      });
    });

    return proxyQueue.catch(function () {
      return fetchWithTimeout(
        url,
        { credentials: 'omit', signal: signal, mode: 'cors' },
        DETAIL_FETCH_TIMEOUT_MS
      ).then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      });
    });
  }

  function cleanText(value) {
    if (value == null) return '';
    return String(value).replace(/\s+/g, ' ').trim();
  }

  function firstValue(values) {
    for (var i = 0; i < values.length; i += 1) {
      var value = cleanText(values[i]);
      if (value) return value;
    }
    return '';
  }

  function toMetaRows(machine) {
    var rows = [];
    var year = machine && machine.year;
    var hours = machine && machine.hours;
    var location = firstValue([machine && machine.location, machine && machine.city, machine && machine.country]);
    var brand = firstValue([machine && machine.brand, machine && machine.make]);
    var category = firstValue([machine && machine.categoryName, machine && machine.category, machine && machine.catalogName]);

    if (brand) rows.push({ label: 'Mærke', value: brand });
    if (year) rows.push({ label: 'Årgang', value: String(year) });
    if (hours) rows.push({ label: 'Timer', value: new Intl.NumberFormat('da-DK').format(Number(hours)) });
    if (location) rows.push({ label: 'Lokation', value: location });
    if (category) rows.push({ label: 'Kategori', value: category });
    return rows;
  }

  function toDetailMetaRows(machine) {
    var rows = [];
    var year = machine && machine.year;
    var hours = machine && machine.hours;
    var brand = firstValue([machine && machine.brand, machine && machine.make]);

    if (brand) rows.push({ label: 'Mærke', value: brand });
    if (year) rows.push({ label: 'Årgang', value: String(year) });
    if (hours) rows.push({ label: 'Timer', value: new Intl.NumberFormat('da-DK').format(Number(hours)) });
    return rows;
  }

  function toCardMetaRows(machine, cardDisplay) {
    var rows = [];
    var year = machine && machine.year;
    var location = firstValue([machine && machine.location, machine && machine.city, machine && machine.country]);
    var brand = firstValue([machine && machine.brand, machine && machine.make]);

    if (cardDisplay.brand && brand) rows.push({ label: 'Mærke', value: brand });
    if (cardDisplay.year && year) rows.push({ label: 'Årgang', value: String(year) });
    if (cardDisplay.location && location) rows.push({ label: 'Lokation', value: location });

    return rows;
  }

  function machineFilterCategory(machine) {
    return firstValue([machine && machine.category, machine && machine.categoryName, machine && machine.catalogName]);
  }

  /**
   * Kilden blander to kategorisystemer: 49 maskiner har danske maskinkategorier
   * ("Minigravere", "Læssemaskiner > Minilæssere"), mens 3 har engelsk
   * top/underkategori ("construction / miniexcavators").
   *
   * Filtrerede vi på den engelske top, fik vi "Entreprenør" med 2 maskiner —
   * mens de 20 minigravere lå for sig under "Minigravere". Derfor oversættes
   * den engelske UNDERkategori til den tilsvarende danske maskinkategori, så
   * alt havner ét sted. Ukendte nøgler falder tilbage på underkategorien selv.
   */
  var ENGELSK_UNDERKATEGORI = {
    miniexcavators: 'Minigravere',
    excavators: 'Gravemaskiner',
    telehandlers: 'Læssemaskiner',
    wheelloaders: 'Læssemaskiner',
    skidsteerloaders: 'Læssemaskiner',
    backhoeloaders: 'Rendegravere',
    dumpers: 'Dumpere',
    atvs: 'ATV',
    trailers: 'Anhængere og trailere',
    agriothers: 'Redskaber',
    othermachines: 'Redskaber'
  };

  var TOP_CATEGORY_LABELS = {};
  var TOP_CATEGORY_ORDER = [];

  function toTopCategoryKey(rawCategory) {
    var value = cleanText(rawCategory).toLowerCase();
    if (!value) return '';

    // Engelsk system ("construction / miniexcavators"): brug UNDERkategorien
    // oversat til dansk maskinkategori — ellers samles alt under to brede
    // kategorier, som ikke svarer til resten af listen.
    if (value.indexOf('/') > -1) {
      var under = value.split('/').pop().trim().replace(/[\s-]/g, '');
      if (ENGELSK_UNDERKATEGORI[under]) return ENGELSK_UNDERKATEGORI[under].toLowerCase();
      return under;
    }

    // Dansk system: "Læssemaskiner > Gummihjulslæssere" → "læssemaskiner", så de
    // tre læssemaskin-varianter bliver én filterknap i stedet for tre lange.
    return value.split('>')[0].trim();
  }

  function toTopCategoryLabel(categoryKey) {
    if (TOP_CATEGORY_LABELS[categoryKey]) return TOP_CATEGORY_LABELS[categoryKey];
    var normalized = cleanText(categoryKey).replace(/[-_]+/g, ' ');
    if (!normalized) return '';
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  }

  function mount(root) {
    var shop = root.getAttribute('data-shop');
    var limit = Math.max(6, parseInt(root.getAttribute('data-limit') || '12', 10) || 12);
    var detailPath = root.getAttribute('data-detail-page-path') || '/pages/brugte-maskiner';
    var defaultSort = root.getAttribute('data-default-sort') || 'newest';
    var inquiryPriceText = cleanText(root.getAttribute('data-price-inquiry-text')) || 'Send forespørgelse for pris';
    var vatLabelText = cleanText(root.getAttribute('data-price-vat-label'));
    moneyFormat = cleanText(root.getAttribute('data-money-format'));

    /**
     * Renders a machine price into `el` the way products render theirs: the amount followed by
     * the ex-VAT label. Inquiry/empty prices are plain text — no VAT claim without an amount.
     */
    function renderPrice(el, amountText, isInquiry) {
      if (!el) return;
      el.textContent = '';
      if (isInquiry || !amountText) {
        el.textContent = isInquiry ? inquiryPriceText : '';
        return;
      }
      el.appendChild(document.createTextNode(amountText));
      if (!vatLabelText) return;
      var label = document.createElement('small');
      label.className = 'price__vat-label';
      label.textContent = vatLabelText;
      el.appendChild(label);
    }

    if (!shop) return;

    var listView = qs(root, '[data-um-list-view]');
    var loadingEl = qs(root, '[data-um-loading]');
    var errorEl = qs(root, '[data-um-error]');
    var gridEl = document.getElementById('MachinesGrid');
    var pagerEl = qs(root, '[data-um-pager]');
    var cardTpl = document.getElementById('MachineCardTemplate');

    var detailView = qs(root, '[data-um-detail-view]');
    var detailLoadingEl = qs(root, '[data-um-detail-loading]');
    var detailErrorEl = qs(root, '[data-um-detail-error]');
    var detailInnerEl = qs(root, '[data-um-detail-inner]');
    var backLinkEl = qs(root, '[data-um-back]');
    var categoryFilterEl = qs(root, '[data-um-category-filter]');
    var categorySelectEl = qs(root, '[data-um-category-select]');

    var filterSearch = document.getElementById('FilterSearch');
    var filterCategory = document.getElementById('FilterCategory');
    var applyBtn = document.getElementById('ApplyFilters');

    if (!gridEl || !listView || !detailView || !cardTpl) return;

    var state = getListState(defaultSort);
    var facets = { categoryOptions: [] };
    var facetsLoaded = false;
    var total = 0;
    var totalPages = 1;
    var allMachines = [];
    var displayedMachines = [];
    var listCache = new Map();
    var detailHydrateCache = new Map();
    var listAbort = null;
    var detailAbort = null;
    var cardDisplay = {
      category: root.getAttribute('data-show-card-category') !== 'false',
      brand: root.getAttribute('data-show-card-brand') !== 'false',
      year: root.getAttribute('data-show-card-year') !== 'false',
      location: root.getAttribute('data-show-card-location') !== 'false',
      description: root.getAttribute('data-show-card-description') !== 'false',
      price: root.getAttribute('data-show-card-price') !== 'false'
    };

    if (!filterSearch && !filterCategory && !applyBtn) {
      state.q = '';
      facetsLoaded = true;
    }

    function proxyBase() {
      return '/apps/ib-konfigurator/machines';
    }

    function buildQuery(includeFacets, pageOverride) {
      var p = new URLSearchParams();
      var page = Math.max(1, parseInt(pageOverride || state.page || 1, 10) || 1);
      p.set('shop', shop);
      p.set('limit', String(limit));
      p.set('page', String(page));
      if (includeFacets) p.set('includeFacets', '1');
      if (state.sort) p.set('sort', state.sort);
      return p.toString();
    }

    function cloneCard() {
      if (!cardTpl || !cardTpl.content) return null;
      return document.importNode(cardTpl.content, true);
    }

    function fillCard(frag, m) {
      var href = buildDetailUrl(detailPath, m.id);
      var cardImages = extractMachineImages(m);
      var imgUrl = cardImages[0] || '';
      var priceText = formatPrice(m.price, m.currency) || '';
      var showInquiryPrice = shouldShowInquiryPrice(m.price);
      var category = firstValue([m.catalogName, m.category, m.categoryName]);
      var title = cleanText(m.title) || 'Maskine';
      var descriptionText = getMachineDescription(m);
      var metaRows = toCardMetaRows(m, cardDisplay);

      var imgEl = frag.querySelector('[data-image]');
      var linkEl = frag.querySelector('[data-link]');
      var linkTitleEl = frag.querySelector('[data-link-title]');
      var priceEl = frag.querySelector('[data-price]');
      var priceWrapEl = frag.querySelector('[data-card-price-wrap]');
      var categoryEl = frag.querySelector('[data-card-category]');
      var descriptionEl = frag.querySelector('[data-card-description]');
      var metaEl = frag.querySelector('[data-card-meta]');

      if (imgEl) {
        if (imgUrl) {
          imgEl.referrerPolicy = 'no-referrer';
          imgEl.src = imgUrl;
          imgEl.alt = title;
          imgEl.hidden = false;
        } else {
          imgEl.removeAttribute('src');
          imgEl.alt = '';
          imgEl.hidden = true;
        }
      }
      if (linkEl) {
        linkEl.href = href;
        linkEl.setAttribute('aria-label', title);
      }
      if (linkTitleEl) {
        linkTitleEl.href = href;
        linkTitleEl.textContent = title;
      }
      if (categoryEl) {
        // Vis den normaliserede top-kategori, ikke råværdien: kilden leverer
        // bl.a. "agriculture / agriothers", som ellers blev vist som "agriothers".
        var categoryLabel = toTopCategoryLabel(toTopCategoryKey(category)) || cleanText(category);
        categoryEl.textContent = categoryLabel;
        categoryEl.hidden = !cardDisplay.category || !categoryLabel;
      }
      if (descriptionEl) {
        descriptionEl.textContent = descriptionText;
        descriptionEl.hidden = !cardDisplay.description || !descriptionText;
      }
      if (metaEl) {
        if (metaRows.length) {
          metaEl.innerHTML = metaRows
            .map(function (row) {
              return '<li>' + esc(row.label + ': ' + row.value) + '</li>';
            })
            .join('');
          metaEl.hidden = false;
        } else {
          metaEl.innerHTML = '';
          metaEl.hidden = true;
        }
      }
      if (priceWrapEl) priceWrapEl.hidden = !cardDisplay.price;
      renderPrice(priceEl, priceText, showInquiryPrice);
    }

    function populateCategories() {
      if (!filterCategory) return;
      var current = state.categoryName;
      var opts = facets.categoryOptions || [];
      var seen = new Set();
      filterCategory.innerHTML = '<option value="">All categories</option>';
      opts.forEach(function (o) {
        var name = typeof o === 'string' ? o : o.categoryName;
        if (!name || seen.has(name)) return;
        seen.add(name);
        var opt = document.createElement('option');
        opt.value = name;
        opt.textContent =
          typeof o === 'object' && o.catalogName ? o.catalogName + ' / ' + name : name;
        if (current === name) opt.selected = true;
        filterCategory.appendChild(opt);
      });
    }

    function syncFiltersFromState() {
      if (filterSearch) filterSearch.value = state.q;
      if (filterCategory) filterCategory.value = state.categoryName;
    }

    function applyMachineFilters() {
      var activeCategoryKey = toTopCategoryKey(state.categoryName);
      state.categoryName = activeCategoryKey;

      if (!allMachines.length) {
        displayedMachines = [];
      } else if (activeCategoryKey) {
        displayedMachines = allMachines.filter(function (machine) {
          return toTopCategoryKey(machineFilterCategory(machine)) === activeCategoryKey;
        });
      } else {
        displayedMachines = allMachines.slice();
      }
      total = displayedMachines.length;
      totalPages = 1;
      renderCards();
      renderPager();
      writeListStateToUrl(state);
    }

    function deriveFilterCategories(data, machines) {
      var keys = Array.from(
        new Set(
          (machines || [])
            .map(function (machine) {
              return toTopCategoryKey(machineFilterCategory(machine));
            })
            .filter(Boolean)
        )
      );

      var orderedKnown = TOP_CATEGORY_ORDER.filter(function (key) {
        return keys.indexOf(key) > -1;
      });
      var dynamicUnknown = keys
        .filter(function (key) {
          return TOP_CATEGORY_ORDER.indexOf(key) === -1;
        })
        .sort(function (a, b) {
          return a.localeCompare(b, 'da');
        });
      return orderedKnown.concat(dynamicUnknown);
    }

    function renderCategoryButtons(data, machines) {
      if (!categoryFilterEl || !categorySelectEl) return;

      var categories = deriveFilterCategories(data, machines);
      if (!categories.length) {
        categorySelectEl.innerHTML = '';
        categoryFilterEl.hidden = true;
        return;
      }

      var aktiv = toTopCategoryKey(state.categoryName);
      categorySelectEl.innerHTML =
        '<option value=""' + (aktiv ? '' : ' selected') + '>' + esc('Alle') + '</option>' +
        categories
          .map(function (categoryKey) {
            var label = toTopCategoryLabel(categoryKey) || categoryKey;
            return (
              '<option value="' + esc(categoryKey) + '"' +
              (aktiv === categoryKey ? ' selected' : '') +
              '>' + esc(label) + '</option>'
            );
          })
          .join('');

      if (!categorySelectEl.dataset.umBound) {
        categorySelectEl.dataset.umBound = '1';
        categorySelectEl.addEventListener('change', function () {
          state.categoryName = categorySelectEl.value || '';
          applyMachineFilters();
        });
      }

      categoryFilterEl.hidden = false;
    }

    function renderPager() {
      if (!pagerEl) return;
      pagerEl.innerHTML = '';
      pagerEl.hidden = true;
    }

    function renderCards() {
      gridEl.innerHTML = '';
      if (!displayedMachines.length) {
        gridEl.hidden = false;
        gridEl.innerHTML =
          '<li class="product-grid__item product-grid__item--um-empty" role="presentation">' +
          '<p class="paragraph">' +
          esc('Ingen maskiner matcher de valgte filtre.') +
          '</p></li>';
        return;
      }
      var frag = document.createDocumentFragment();
      displayedMachines.forEach(function (m) {
        var node = cloneCard();
        if (!node) return;
        fillCard(node, m);
        frag.appendChild(node);
      });
      gridEl.appendChild(frag);
      gridEl.hidden = false;
    }

    function showList() {
      detailView.hidden = true;
      listView.hidden = false;
      if (detailInnerEl) detailInnerEl.hidden = true;
    }

    function showDetail() {
      listView.hidden = true;
      detailView.hidden = false;
    }

    function finishListResponse(data) {
      allMachines = Array.isArray(data.machines) ? data.machines : [];
      renderCategoryButtons(data, allMachines);
      applyMachineFilters();
    }

    function fetchList() {
      showList();
      state.page = 1;
      if (loadingEl) loadingEl.hidden = false;
      if (gridEl) gridEl.hidden = true;
      if (errorEl) errorEl.hidden = true;

      if (listAbort) listAbort.abort();
      listAbort = new AbortController();

      var includeFacets = !facetsLoaded;
      var query = buildQuery(includeFacets, 1);
      var url = proxyBase() + '?' + query;
      var cacheKey = 'all:' + query.replace(/([?&])page=\d+/, '$1page=1');

      if (window.__IB_UM_PREFETCH__ && window.__IB_UM_PREFETCH__.url === url) {
        var pf = window.__IB_UM_PREFETCH__;
        if (Date.now() - pf.ts < 300000 && pf.data) {
          listCache.set(cacheKey, { ts: pf.ts, data: pf.data });
        }
      }

      if (listCache.has(cacheKey)) {
        var cached = listCache.get(cacheKey);
        if (Date.now() - cached.ts < 60_000) {
          if (loadingEl) loadingEl.hidden = true;
          var d = cached.data;
          if (d.facets && includeFacets) {
            facets = d.facets;
            facetsLoaded = true;
            populateCategories();
            syncFiltersFromState();
          }
          finishListResponse(d);
          return;
        }
      }

      var fetchPage = function (page, includePageFacets) {
        return fetchJson(proxyBase() + '?' + buildQuery(includePageFacets, page), {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          signal: listAbort.signal
        });
      };

      fetchPage(1, includeFacets)
        .then(function (firstData) {
          if (firstData && firstData.error) throw new Error(firstData.error);
          var merged = Object.assign({}, firstData);
          var pages = Math.max(1, parseInt(firstData.totalPages || 1, 10) || 1);
          var machines = Array.isArray(firstData.machines) ? firstData.machines.slice() : [];

          if (pages <= 1) {
            merged.machines = machines;
            return merged;
          }

          var pagePromises = [];
          for (var page = 2; page <= pages; page += 1) {
            pagePromises.push(fetchPage(page, false));
          }

          return Promise.all(pagePromises).then(function (results) {
            results.forEach(function (part) {
              if (part && Array.isArray(part.machines)) {
                machines = machines.concat(part.machines);
              }
            });
            merged.machines = machines;
            merged.total = machines.length;
            merged.totalPages = 1;
            return merged;
          });
        })
        .then(function (data) {
          if (loadingEl) loadingEl.hidden = true;
          if (data.error) throw new Error(data.error);
          if (data.facets && includeFacets) {
            facets = data.facets;
            facetsLoaded = true;
            populateCategories();
            syncFiltersFromState();
          } else if (!facetsLoaded && data.facets) {
            facets = data.facets;
            facetsLoaded = true;
            populateCategories();
            syncFiltersFromState();
          }
          listCache.set(cacheKey, { ts: Date.now(), data: data });
          try {
            if (
              state.page === 1 &&
              state.sort === defaultSort &&
              !state.q &&
              !state.categoryName
            ) {
              sessionStorage.setItem(
                'ib-um-ss:' + shop,
                JSON.stringify({ ts: Date.now(), url: url, data: data })
              );
            }
          } catch (_) {}
          finishListResponse(data);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          if (loadingEl) loadingEl.hidden = true;
          if (gridEl) gridEl.hidden = true;
          if (errorEl) {
            errorEl.hidden = false;
            errorEl.textContent = 'Kunne ikke hente maskiner lige nu. Prøv igen om lidt.';
          }
        });
    }

    function renderDetail(machine) {
      var img = document.getElementById('DetailImage');
      var title = document.getElementById('DetailTitle');
      var price = document.getElementById('DetailPrice');
      var description = document.getElementById('DetailDescription');
      var meta = document.getElementById('DetailMeta');
      var descriptionHeading = document.getElementById('DetailDescriptionHeading');
      var thumbs = qs(detailView, '[data-um-detail-thumbs]');
      var galleryNote = qs(detailView, '[data-um-detail-gallery-note]');
      var externalLink = document.getElementById('DetailExternalLink');

      var images = extractMachineImages(machine);
      var titleText = cleanText(machine.title) || 'Maskine';
      var machineDescription = extractMachineDescription(machine);
      var descriptionText = cleanText(machineDescription);
      var metaRows = toDetailMetaRows(machine);
      var sourceUrl = firstValue([machine.url, machine.sourceUrl]);
      var showInquiryPrice = shouldShowInquiryPrice(machine.price);
      var announcedImageCount = extractMachineImageCount(machine, images.length);
      var resolvedImageCount = images.length;

      function setActiveImage(url) {
        if (!img) return;
        if (url) {
          img.referrerPolicy = 'no-referrer';
          img.src = url;
          img.alt = titleText;
          img.hidden = false;
        } else {
          img.removeAttribute('src');
          img.alt = '';
          img.hidden = true;
        }
      }

      setActiveImage(images[0] || '');

      if (thumbs) {
        if (images.length > 1) {
          thumbs.innerHTML = images
            .map(function (imageUrl, index) {
              return (
                '<li><button type="button" data-um-thumb-index="' +
                String(index) +
                '"><img src="' +
                esc(imageUrl) +
                '" alt="' +
                esc(titleText) +
                '" loading="lazy" decoding="async" referrerpolicy="no-referrer"></button></li>'
              );
            })
            .join('');
          thumbs.hidden = false;
          thumbs.querySelectorAll('[data-um-thumb-index]').forEach(function (buttonEl) {
            buttonEl.addEventListener('click', function () {
              var i = Number(buttonEl.getAttribute('data-um-thumb-index') || '0');
              setActiveImage(images[i] || images[0] || '');
            });
          });
        } else {
          thumbs.innerHTML = '';
          thumbs.hidden = true;
        }
      }

      if (title) title.textContent = titleText;
      renderPrice(price, formatPrice(machine.price, machine.currency) || '', showInquiryPrice);
      if (meta) {
        if (metaRows.length) {
          meta.innerHTML = metaRows
            .map(function (row) {
              return '<li><strong>' + esc(row.label) + ':</strong> ' + esc(row.value) + '</li>';
            })
            .join('');
          meta.hidden = false;
        } else {
          meta.innerHTML = '';
          meta.hidden = true;
        }
      }
      if (description) {
        description.innerHTML = descriptionText
          ? String(machineDescription)
          : '<p>Ingen yderligere oplysninger fra Mascus på denne maskine.</p>';
      }
      if (descriptionHeading) {
        descriptionHeading.hidden = false;
      }
      if (galleryNote) {
        if (announcedImageCount > resolvedImageCount) {
          galleryNote.textContent =
            'Mascus angiver ' +
            String(announcedImageCount) +
            ' billeder, men feedet leverer aktuelt ' +
            String(resolvedImageCount) +
            '.';
          galleryNote.hidden = false;
        } else {
          galleryNote.textContent = '';
          galleryNote.hidden = true;
        }
      }
      if (externalLink) {
        if (sourceUrl) {
          externalLink.href = sourceUrl;
          externalLink.hidden = false;
        } else {
          externalLink.removeAttribute('href');
          externalLink.hidden = true;
        }
      }
      if (detailInnerEl) detailInnerEl.hidden = false;
    }

    function tryHydrateDetailImages(machine) {
      if (!machine || !machine.id) return Promise.resolve(machine);
      var currentImages = extractMachineImages(machine);
      var announcedImageCount = extractMachineImageCount(machine, currentImages.length);
      var detailUrl = machine.detailUrl || machine.url || '';

      if (
        !detailUrl ||
        (currentImages.length >= announcedImageCount && hasMeaningfulDescription(machine))
      ) {
        return Promise.resolve(machine);
      }

      if (detailHydrateCache.has(machine.id)) {
        var cached = detailHydrateCache.get(machine.id);
        if (cached.images && cached.images.length > currentImages.length) {
          machine.images = cached.images;
        }
        if (cached.descriptionHtml && !hasMeaningfulDescription(machine)) {
          machine.descriptionHtml = cached.descriptionHtml;
        }
        return Promise.resolve(machine);
      }

      return fetchMascusHtml(shop, detailUrl, detailAbort && detailAbort.signal)
        .then(function (html) {
          applyEnrichmentFromHtml(machine, html);
          detailHydrateCache.set(machine.id, {
            images: extractMachineImages(machine),
            descriptionHtml: machine.descriptionHtml || ''
          });
          return machine;
        })
        .catch(function () {
          detailHydrateCache.set(machine.id, {
            images: currentImages,
            descriptionHtml: ''
          });
          return machine;
        });
    }

    function fetchDetail(machineId) {
      showDetail();
      if (detailLoadingEl) detailLoadingEl.hidden = false;
      if (detailErrorEl) detailErrorEl.hidden = true;
      if (detailInnerEl) detailInnerEl.hidden = true;

      if (detailAbort) detailAbort.abort();
      detailAbort = new AbortController();

      var url = proxyBase() + '?shop=' + encodeURIComponent(shop) + '&id=' + encodeURIComponent(machineId);

      fetchJson(url, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: detailAbort.signal
      })
        .then(function (data) {
          if (detailLoadingEl) detailLoadingEl.hidden = true;
          if (!data || !data.machine) throw new Error('not found');
          if (data.machine.mascusHtml && typeof data.machine.mascusHtml === 'string') {
            applyEnrichmentFromHtml(data.machine, data.machine.mascusHtml);
          }
          renderDetail(data.machine);
          return tryHydrateDetailImages(data.machine).then(function (enrichedMachine) {
            renderDetail(enrichedMachine);
          });
        })
        .then(function () {
          if (detailView && detailView.scrollIntoView) {
            detailView.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          if (detailLoadingEl) detailLoadingEl.hidden = true;
          if (detailErrorEl) {
            detailErrorEl.hidden = false;
            detailErrorEl.textContent = 'Maskinen kunne ikke indlæses.';
          }
        });
    }

    function clearMachineParam() {
      var u = new URL(window.location.href);
      u.searchParams.delete('machine');
      u.searchParams.delete('slug');
      window.history.pushState({}, '', u.toString());
    }

    if (backLinkEl) {
      backLinkEl.addEventListener('click', function (e) {
        e.preventDefault();
        clearMachineParam();
        showList();
        fetchList();
      });
    }

    if (applyBtn) {
      applyBtn.addEventListener('click', function () {
        state.q = filterSearch ? filterSearch.value.trim() : '';
        state.categoryName = filterCategory ? filterCategory.value : '';
        state.page = 1;
        fetchList();
      });
    }

    if (filterSearch) {
      filterSearch.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          if (applyBtn) applyBtn.click();
        }
      });
    }

    if (gridEl) {
      gridEl.addEventListener('click', function (e) {
        var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
        if (!a || !gridEl.contains(a)) return;
        var href = a.getAttribute('href') || '';
        if (href.indexOf('machine=') === -1) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        if (a.getAttribute('target') === '_blank') return;
        e.preventDefault();
        var u;
        try {
          u = new URL(href, window.location.href);
        } catch (_) {
          return;
        }
        var id = u.searchParams.get('machine') || '';
        window.history.pushState({}, '', u.pathname + u.search);
        fetchDetail(id);
      });
    }

    function onUsedMachinesPopState() {
      var id = getMachineFromUrl();
      if (id) {
        fetchDetail(id);
      } else {
        showList();
        fetchList();
      }
    }
    ibUmPopstateCallbacks.push(onUsedMachinesPopState);

    var machineIdFromUrl = getMachineFromUrl();
    if (machineIdFromUrl) {
      fetchDetail(machineIdFromUrl);
    } else {
      syncFiltersFromState();
      fetchList();
    }
  }

  (function hydrateUsedMachinesCache() {
    var root = document.querySelector('[data-um-storefront]');
    if (!root) return;
    var shop = root.getAttribute('data-shop');
    if (!shop) return;
    try {
      var sk = 'ib-um-ss:' + shop;
      var prev = sessionStorage.getItem(sk);
      if (prev) {
        var parsed = JSON.parse(prev);
        if (parsed && parsed.ts && Date.now() - parsed.ts < 300000 && parsed.url && parsed.data) {
          window.__IB_UM_PREFETCH__ = parsed;
        }
      }
    } catch (_) {}
  })();

  document.querySelectorAll('[data-um-storefront]').forEach(mount);

  if (typeof window !== 'undefined') {
    window.__IB_UM_EXTRACT__ = {
      extractImageUrlsFromDetailHtml: extractImageUrlsFromDetailHtml,
      extractLdProductExtras: extractLdProductExtras,
      extractFromJinaMarkdownFallback: extractFromJinaMarkdownFallback,
      applyEnrichmentFromHtml: applyEnrichmentFromHtml,
      hasMeaningfulDescription: hasMeaningfulDescription,
      isGarbageProductDescription: isGarbageProductDescription,
      normalizeImageUrlForDisplay: normalizeImageUrlForDisplay,
      unwrapMascusImageUrl: unwrapMascusImageUrl
    };
  }
})();
